function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/**
 * Script editor for large blocks of code
 */
import React, { Component } from 'react';
import { LoadingOverlay, ShortcutRegistry } from '@deephaven/components';
import Log from '@deephaven/log';
import { assertNotNull } from '@deephaven/utils';
import Editor from "./Editor.js";
import { MonacoProviders, MonacoUtils } from "../monaco/index.js";
import "./ScriptEditor.css";
import SHORTCUTS from "../ConsoleShortcuts.js";
import ScriptEditorUtils from "./ScriptEditorUtils.js";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
var log = Log.module('ScriptEditor');
class ScriptEditor extends Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "contextActionCleanups", void 0);
    _defineProperty(this, "completionCleanup", void 0);
    _defineProperty(this, "editor", void 0);
    _defineProperty(this, "editorComponent", void 0);
    this.handleEditorInitialized = this.handleEditorInitialized.bind(this);
    this.handleEditorWillDestroy = this.handleEditorWillDestroy.bind(this);
    this.handleRun = this.handleRun.bind(this);
    this.handleRunSelected = this.handleRunSelected.bind(this);
    this.updateShortcuts = this.updateShortcuts.bind(this);
    this.contextActionCleanups = [];
    this.editorComponent = /*#__PURE__*/React.createRef();
    this.state = {
      model: null
    };
  }
  componentDidMount() {
    ShortcutRegistry.addEventListener('onUpdate', this.updateShortcuts);
  }
  componentDidUpdate(prevProps) {
    var _prevProps$settings, _prevProps$settings2;
    var _this$props = this.props,
      sessionLanguage = _this$props.sessionLanguage,
      settings = _this$props.settings;
    var language = settings === null || settings === void 0 ? void 0 : settings.language;
    var languageChanged = language !== ((_prevProps$settings = prevProps.settings) === null || _prevProps$settings === void 0 ? void 0 : _prevProps$settings.language);
    if (languageChanged) {
      log.debug('Set language', language);
      this.setLanguage(language);
    }
    var sessionDisconnected = sessionLanguage == null && prevProps.sessionLanguage != null;
    var languageMatch = language === sessionLanguage;
    var prevLanguageMatch = ((_prevProps$settings2 = prevProps.settings) === null || _prevProps$settings2 === void 0 ? void 0 : _prevProps$settings2.language) === prevProps.sessionLanguage;
    if (sessionDisconnected || sessionLanguage !== undefined && prevLanguageMatch && !languageMatch) {
      // Session disconnected or language changed from matching the session language to non-matching
      log.debug('De-init completion');
      this.deInitCodeCompletion();
    }
    var sessionConnected = sessionLanguage != null && prevProps.sessionLanguage == null;
    if (sessionConnected && languageMatch || sessionLanguage !== undefined && !prevLanguageMatch && languageMatch) {
      // Session connected with a matching language or notebook language changed to matching
      log.debug('Init completion');
      this.initCodeCompletion();
    }
  }
  componentWillUnmount() {
    ShortcutRegistry.removeEventListener('onUpdate', this.updateShortcuts);
  }
  getValue() {
    if (this.editor) {
      return this.editor.getValue();
    }
    log.error('Editor not initialized');
    return null;
  }
  getSelectedCommand() {
    var _this$editor, _this$editor2;
    var range = (_this$editor = this.editor) === null || _this$editor === void 0 ? void 0 : _this$editor.getSelection();
    assertNotNull(range);
    var model = (_this$editor2 = this.editor) === null || _this$editor2 === void 0 ? void 0 : _this$editor2.getModel();
    assertNotNull(model);
    var startLineNumber = range.startLineNumber,
      endColumn = range.endColumn;
    var endLineNumber = range.endLineNumber;
    if (endColumn === 1 && endLineNumber > startLineNumber) {
      endLineNumber -= 1;
    }
    var startLineMinColumn = model.getLineMinColumn(startLineNumber);
    var endLineMaxColumn = model.getLineMaxColumn(endLineNumber);
    var wholeLineRange = range.setStartPosition(startLineNumber, startLineMinColumn).setEndPosition(endLineNumber, endLineMaxColumn);
    return ScriptEditorUtils.outdentCode(model.getValueInRange(wholeLineRange));
  }
  handleEditorInitialized(innerEditor) {
    var _this$props2 = this.props,
      focusOnMount = _this$props2.focusOnMount,
      onChange = _this$props2.onChange,
      onEditorInitialized = _this$props2.onEditorInitialized,
      settings = _this$props2.settings,
      session = _this$props2.session,
      sessionLanguage = _this$props2.sessionLanguage;
    log.debug('handleEditorInitialized', sessionLanguage, session, settings);
    this.editor = innerEditor;
    this.setState({
      model: this.editor.getModel()
    });
    MonacoUtils.setEOL(innerEditor);
    MonacoUtils.registerPasteHandler(innerEditor);

    // Always initialize context actions when the editor is created to ensure that unwanted default
    // OS shortcuts are overridden by custom shortcuts.
    this.initContextActions();
    if (session != null && settings && sessionLanguage === settings.language) {
      this.initCodeCompletion();
    }
    innerEditor.onDidChangeModelContent(onChange);
    if (focusOnMount != null && focusOnMount) {
      innerEditor.focus();
    }
    onEditorInitialized(this.editor);
  }
  handleEditorWillDestroy(innerEditor) {
    log.debug('handleEditorWillDestroy');
    var onEditorWillDestroy = this.props.onEditorWillDestroy;
    onEditorWillDestroy(innerEditor);
    this.deInitContextActions();
    this.deInitCodeCompletion();
    this.setState({
      model: null
    });
    this.editor = undefined;
  }
  handleRun() {
    var _this$props3 = this.props,
      onRunCommand = _this$props3.onRunCommand,
      session = _this$props3.session,
      sessionLanguage = _this$props3.sessionLanguage,
      settings = _this$props3.settings;
    var command = this.getValue();
    var language = settings === null || settings === void 0 ? void 0 : settings.language;
    var languageMatch = language === sessionLanguage;
    if (session == null || !languageMatch) {
      log.info("Run disabled - ".concat(session == null ? 'session disconnected' : 'language mismatch'));
      return;
    }
    if (command != null) {
      onRunCommand(command);
    }
  }
  handleRunSelected() {
    var _this$props4 = this.props,
      onRunCommand = _this$props4.onRunCommand,
      session = _this$props4.session,
      sessionLanguage = _this$props4.sessionLanguage,
      settings = _this$props4.settings;
    var command = this.getSelectedCommand();
    var language = settings === null || settings === void 0 ? void 0 : settings.language;
    var languageMatch = language === sessionLanguage;
    if (session == null || !languageMatch) {
      log.info("Run selected disabled - ".concat(session == null ? 'session disconnected' : 'language mismatch'));
      return;
    }
    onRunCommand(command);
  }
  initContextActions() {
    if (this.contextActionCleanups.length > 0) {
      log.error('Context actions already initialized.');
      return;
    }
    if (!this.editor) {
      return;
    }
    var cleanups = [];
    cleanups.push(this.editor.addAction({
      id: 'run-code',
      label: 'Run',
      keybindings: [MonacoUtils.getMonacoKeyCodeFromShortcut(SHORTCUTS.NOTEBOOK.RUN)],
      contextMenuGroupId: 'navigation',
      contextMenuOrder: 1.5,
      run: () => {
        this.handleRun();
      }
    }));
    cleanups.push(this.editor.addAction({
      id: 'run-selected-code',
      label: 'Run Selected',
      keybindings: [MonacoUtils.getMonacoKeyCodeFromShortcut(SHORTCUTS.NOTEBOOK.RUN_SELECTED)],
      contextMenuGroupId: 'navigation',
      contextMenuOrder: 1.5,
      run: () => {
        this.handleRunSelected();
      }
    }));
    this.contextActionCleanups = cleanups;
  }
  deInitContextActions() {
    if (this.contextActionCleanups.length > 0) {
      this.contextActionCleanups.forEach(cleanup => cleanup.dispose());
      this.contextActionCleanups = [];
    }
  }
  updateShortcuts() {
    this.deInitContextActions();
    this.initContextActions();
  }
  initCodeCompletion() {
    if (this.completionCleanup != null) {
      log.error('Code completion already initialized.');
      return;
    }
    var session = this.props.session;
    log.debug('initCodeCompletion', this.editor, session);
    if (this.editor && session != null) {
      this.completionCleanup = MonacoUtils.openDocument(this.editor, session);
    }
  }
  deInitCodeCompletion() {
    var session = this.props.session;
    log.debug('deInitCodeCompletion', this.editor, session);
    if (this.completionCleanup) {
      this.completionCleanup.dispose();
      this.completionCleanup = undefined;
      if (this.editor && session != null) {
        MonacoUtils.closeDocument(this.editor, session);
      }
    }
  }
  append(text) {
    var focus = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    assertNotNull(this.editor);
    var model = this.editor.getModel();
    assertNotNull(model);
    var currentText = model.getValue();
    if (currentText) {
      model.setValue("".concat(currentText, "\n").concat(text));
    } else {
      model.setValue("".concat(text));
    }
    var lastLine = model.getLineCount();
    var column = model.getLineLength(lastLine) + 1;
    this.editor.setPosition({
      lineNumber: lastLine,
      column
    });
    if (focus) {
      this.editor.focus();
    }
  }
  updateDimensions() {
    log.debug('updateDimensions');
    if (this.editor) {
      this.editor.layout();
    }
  }
  focus() {
    log.debug('focus');
    if (this.editor) {
      this.editor.focus();
    }
  }
  toggleFind() {
    if (this.editorComponent.current) {
      this.editorComponent.current.toggleFind();
    }
  }
  setLanguage(language) {
    if (this.editorComponent.current && language !== undefined) {
      this.editorComponent.current.setLanguage(language);
    }
  }
  render() {
    var _settings$language;
    var _this$props5 = this.props,
      error = _this$props5.error,
      isLoaded = _this$props5.isLoaded,
      isLoading = _this$props5.isLoading,
      session = _this$props5.session,
      sessionLanguage = _this$props5.sessionLanguage,
      settings = _this$props5.settings;
    var model = this.state.model;
    var errorMessage = error ? "Unable to open document. ".concat(error) : null;
    var editorLanguage = settings ? (_settings$language = settings.language) !== null && _settings$language !== void 0 ? _settings$language : null : null;
    var completionProviderEnabled = model && session && editorLanguage === sessionLanguage;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [(error || isLoading) && /*#__PURE__*/_jsx("div", {
        className: "h-100 w-100 position-relative",
        children: /*#__PURE__*/_jsx(LoadingOverlay, {
          "data-testid": "script-editor-loading",
          errorMessage: errorMessage,
          isLoading: isLoading,
          isLoaded: isLoaded
        })
      }), isLoaded && /*#__PURE__*/_jsx("div", {
        className: "h-100 w-100 script-editor",
        children: settings && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(Editor, {
            ref: this.editorComponent,
            settings: settings,
            onEditorInitialized: this.handleEditorInitialized,
            onEditorWillDestroy: this.handleEditorWillDestroy
          }), completionProviderEnabled != null && completionProviderEnabled && /*#__PURE__*/_jsx(MonacoProviders, {
            model: model,
            language: editorLanguage,
            session: session
          })]
        })
      })]
    });
  }
}
_defineProperty(ScriptEditor, "defaultProps", {
  error: null,
  isLoading: false,
  isLoaded: false,
  focusOnMount: true,
  onChange: () => undefined,
  onEditorInitialized: () => undefined,
  onEditorWillDestroy: () => undefined,
  session: null
});
export default ScriptEditor;
//# sourceMappingURL=ScriptEditor.js.map